import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Branch } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-branches',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './branches.component.html',
  styleUrls: ['../../settings-shared.css', './branches.component.css'],
})
export class BranchesComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');

  protected branchForm: Omit<Branch, 'id'> = {
    code: '',
    name: '',
    city: '',
    status: 'active',
  };

  protected readonly branches = this.settings.scopedBranches;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Branch): void {
    this.editingId.set(row.id);
    this.branchForm = {
      code: row.code,
      name: row.name,
      city: row.city,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.branchForm.code || !this.branchForm.name) {
      this.toastMessage.set('Code and Name are required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      if (!this.settings.updateBranch(editingId, this.branchForm)) {
        this.toastMessage.set(
          'Branch update is not available via the API yet.',
        );
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        return;
      }
      this.toastMessage.set('Branch updated successfully');
    } else {
      this.settings.addBranch(this.branchForm);
      this.toastMessage.set('Branch added successfully');
    }

    this.closeModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteRow(row: Branch): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    if (!this.settings.deleteBranch(row.id)) {
      this.toastMessage.set('Branch delete is not available via the API yet.');
    } else {
      this.toastMessage.set('Branch deleted successfully');
    }
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  private resetForm(): void {
    this.branchForm = {
      code: '',
      name: '',
      city: '',
      status: 'active',
    };
  }
}
